import { FormEvent, useEffect, useMemo, useState } from 'react';
import { api, SiktraApiError } from '../shared/api';
import { formatDateTime } from '../shared/format';
import type { WebsiteAsset, WebsiteNavigationItem, WebsitePage, WebsitePageSummary, WebsitePayload, WebsiteSection, WebsiteSettings } from '../shared/types';

const defaultSection: WebsiteSection = {
  id: 'section_neu',
  type: 'text_block',
  eyebrow: 'Siktra Sleep',
  title: 'Neue Sektion',
  body: 'Bearbeite diesen Text in Siktra Manage.',
};

function errorMessage(error: unknown): string {
  if (error instanceof SiktraApiError) return `${error.message} (${error.status}/${error.code})`;
  if (error instanceof Error) return error.message;
  return 'Unbekannter Fehler';
}

function sectionLabel(section: WebsiteSection): string {
  return `${section.type} · ${section.title || section.eyebrow || section.id}`;
}

function normalizeSlug(value: string): string {
  const slug = value.trim().toLowerCase().replace(/^\/+|\/+$/g, '').replace(/\s+/g, '-');
  return slug || 'neue-seite';
}

export function WebsiteWorkspace({ onNotice, onError }: { onNotice: (message: string) => void; onError: (message: string) => void }) {
  const [payload, setPayload] = useState<WebsitePayload | null>(null);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [page, setPage] = useState<WebsitePage | null>(null);
  const [settingsDraft, setSettingsDraft] = useState<WebsiteSettings | null>(null);
  const [assetDraft, setAssetDraft] = useState<Partial<WebsiteAsset>>({ title: '', url: '', altText: '', sourceType: 'static' });
  const [navigationDraft, setNavigationDraft] = useState<WebsiteNavigationItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [localError, setLocalError] = useState<string | null>(null);

  const pages = payload?.pages ?? [];
  const assets = payload?.assets ?? [];
  const overview = payload?.overview;
  const selectedPageSummary = useMemo(() => pages.find((item) => item.id === selectedPageId) ?? pages[0] ?? null, [pages, selectedPageId]);

  async function loadWebsite(nextSelectedPageId = selectedPageId) {
    setLoading(true);
    setLocalError(null);
    try {
      const nextPayload = await api.websitePayload();
      setPayload(nextPayload);
      setSettingsDraft(nextPayload.settings);
      setNavigationDraft(nextPayload.navigation);
      const targetPage = nextPayload.pages.find((item) => item.id === nextSelectedPageId) ?? nextPayload.pages[0] ?? null;
      setSelectedPageId(targetPage?.id ?? null);
      if (targetPage) {
        setPage(await api.websitePage(targetPage.id));
      } else {
        setPage(null);
      }
    } catch (error) {
      setLocalError(errorMessage(error));
      onError(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadWebsite(null);
  }, []);

  async function selectPage(summary: WebsitePageSummary) {
    setSelectedPageId(summary.id);
    setBusy(true);
    setLocalError(null);
    try {
      setPage(await api.websitePage(summary.id));
    } catch (error) {
      setLocalError(errorMessage(error));
      onError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  function updatePageField<K extends keyof WebsitePage>(key: K, value: WebsitePage[K]) {
    setPage((current) => current ? { ...current, [key]: value } : current);
  }

  function updateSection(index: number, next: WebsiteSection) {
    setPage((current) => {
      if (!current) return current;
      const sections = [...current.sections];
      sections[index] = next;
      return { ...current, sections };
    });
  }

  function addSection(type = 'text_block') {
    setPage((current) => {
      if (!current) return current;
      return {
        ...current,
        sections: [...current.sections, { ...defaultSection, id: `section_${current.sections.length + 1}`, type }],
      };
    });
  }

  function removeSection(index: number) {
    setPage((current) => current ? { ...current, sections: current.sections.filter((_, currentIndex) => currentIndex !== index) } : current);
  }

  function moveSection(index: number, direction: -1 | 1) {
    setPage((current) => {
      if (!current) return current;
      const target = index + direction;
      if (target < 0 || target >= current.sections.length) return current;
      const sections = [...current.sections];
      const [item] = sections.splice(index, 1);
      sections.splice(target, 0, item);
      return { ...current, sections };
    });
  }

  async function savePage() {
    if (!page) return;
    setBusy(true);
    setLocalError(null);
    try {
      const input = {
        title: page.title.trim(),
        slug: normalizeSlug(page.slug),
        seoTitle: page.seoTitle ?? null,
        seoDescription: page.seoDescription ?? null,
        ogImageAssetId: page.ogImageAssetId ?? null,
        sections: page.sections,
      };
      const saved = await api.updateWebsitePage(page.id, input);
      setPage(saved);
      onNotice('Website-Seite wurde als Draft gespeichert.');
      await loadWebsite(saved.id);
    } catch (error) {
      setLocalError(errorMessage(error));
      onError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function createPage() {
    setBusy(true);
    try {
      const created = await api.createWebsitePage({
        title: 'Neue Seite',
        slug: `neue-seite-${Date.now().toString().slice(-5)}`,
        seoTitle: 'Neue Seite – Siktra Sleep',
        seoDescription: 'Neue Siktra Sleep Website-Seite.',
        sections: [defaultSection],
      });
      onNotice('Neue Website-Seite wurde angelegt.');
      await loadWebsite(created.id);
    } catch (error) {
      setLocalError(errorMessage(error));
      onError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function publishPage() {
    if (!page) return;
    setBusy(true);
    try {
      const input = {
        title: page.title.trim(),
        slug: normalizeSlug(page.slug),
        seoTitle: page.seoTitle ?? null,
        seoDescription: page.seoDescription ?? null,
        ogImageAssetId: page.ogImageAssetId ?? null,
        sections: page.sections,
      };
      const saved = await api.updateWebsitePage(page.id, input);
      const published = await api.publishWebsitePage(saved.id);
      setPage(published);
      onNotice('Website-Seite wurde gespeichert und veröffentlicht.');
      await loadWebsite(published.id);
    } catch (error) {
      setLocalError(errorMessage(error));
      onError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function unpublishPage() {
    if (!page) return;
    setBusy(true);
    try {
      const updated = await api.unpublishWebsitePage(page.id);
      setPage(updated);
      onNotice('Website-Seite wurde zurückgezogen.');
      await loadWebsite(updated.id);
    } catch (error) {
      setLocalError(errorMessage(error));
      onError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function duplicatePage() {
    if (!page) return;
    setBusy(true);
    try {
      const duplicated = await api.duplicateWebsitePage(page.id);
      onNotice('Website-Seite wurde dupliziert.');
      await loadWebsite(duplicated.id);
    } catch (error) {
      setLocalError(errorMessage(error));
      onError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function deletePage() {
    if (!page || !window.confirm(`Seite „${page.title}“ wirklich deaktivieren?`)) return;
    setBusy(true);
    try {
      await api.deleteWebsitePage(page.id);
      onNotice('Website-Seite wurde deaktiviert.');
      await loadWebsite(null);
    } catch (error) {
      setLocalError(errorMessage(error));
      onError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function saveSettings(event: FormEvent) {
    event.preventDefault();
    if (!settingsDraft) return;
    setBusy(true);
    try {
      const settings = await api.updateWebsiteSettings(settingsDraft);
      setSettingsDraft(settings);
      onNotice('Website-Einstellungen wurden gespeichert.');
      await loadWebsite(selectedPageId);
    } catch (error) {
      setLocalError(errorMessage(error));
      onError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function createAsset(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await api.createWebsiteAsset({
        title: assetDraft.title || 'Neues Asset',
        altText: assetDraft.altText || assetDraft.title || '',
        url: assetDraft.url || '/assets/site/hero-night.svg',
        sourceType: assetDraft.sourceType || 'static',
        mimeType: assetDraft.mimeType || null,
      });
      setAssetDraft({ title: '', url: '', altText: '', sourceType: 'static' });
      onNotice('Website-Asset wurde angelegt.');
      await loadWebsite(selectedPageId);
    } catch (error) {
      setLocalError(errorMessage(error));
      onError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function saveNavigation() {
    setBusy(true);
    try {
      const navigation = await api.updateWebsiteNavigation(navigationDraft);
      setNavigationDraft(navigation);
      onNotice('Website-Navigation wurde gespeichert.');
      await loadWebsite(selectedPageId);
    } catch (error) {
      setLocalError(errorMessage(error));
      onError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <section className="panel"><h2>Website</h2><p className="muted">Website-Verwaltung wird geladen…</p></section>;
  }

  return (
    <section className="websiteWorkspace stack">
      {localError ? <div className="alert alert--bad">{localError}</div> : null}
      <div className="metricGrid metricGrid--compact">
        <article className="metricCard"><span>Seiten</span><strong>{overview?.pageCount ?? pages.length}</strong><small>gesamt</small></article>
        <article className="metricCard"><span>Veröffentlicht</span><strong>{overview?.publishedCount ?? pages.filter((item) => item.status === 'published').length}</strong><small>public sichtbar</small></article>
        <article className="metricCard"><span>Drafts</span><strong>{overview?.draftCount ?? pages.filter((item) => item.status !== 'published').length}</strong><small>nicht final</small></article>
        <article className="metricCard"><span>Assets</span><strong>{overview?.assetCount ?? assets.length}</strong><small>Bilder/Medien</small></article>
      </div>

      <div className="twoColumns websiteLayout">
        <aside className="panel websitePagesPanel">
          <div className="panelHeader">
            <div>
              <span className="eyebrow">Website CMS</span>
              <h2>Seiten</h2>
            </div>
            <button className="button button--secondary" onClick={createPage} disabled={busy}>Neue Seite</button>
          </div>
          <div className="websitePageList">
            {pages.map((summary) => (
              <button key={summary.id} className={selectedPageSummary?.id === summary.id ? 'websitePageItem websitePageItem--active' : 'websitePageItem'} onClick={() => selectPage(summary)}>
                <strong>{summary.title}</strong>
                <span>/{summary.slug === 'home' ? '' : summary.slug}</span>
                <small>{summary.status}{summary.hasDraft ? ' · Draft' : ''}</small>
              </button>
            ))}
          </div>
        </aside>

        <main className="panel websiteEditorPanel">
          {page ? (
            <>
              <div className="panelHeader">
                <div>
                  <span className="eyebrow">Page Editor</span>
                  <h2>{page.title}</h2>
                  <p className="muted">Zuletzt geändert: {formatDateTime(page.updatedAt ?? undefined)} · „Veröffentlichen“ speichert aktuelle Änderungen direkt mit.</p>
                </div>
                <div className="toolbar toolbar--inline">
                  <a className="button button--ghost" href={`https://siktra-sleep.com/${page.slug === 'home' ? '' : page.slug}`} target="_blank" rel="noreferrer">Öffnen</a>
                  <button className="button button--secondary" onClick={savePage} disabled={busy}>Draft speichern</button>
                  <button className="button" onClick={publishPage} disabled={busy}>Veröffentlichen</button>
                </div>
              </div>

              <div className="formGrid">
                <label>Titel<input value={page.title} onChange={(event) => updatePageField('title', event.target.value)} /></label>
                <label>Slug<input value={page.slug} onChange={(event) => updatePageField('slug', normalizeSlug(event.target.value))} /></label>
                <label>SEO-Titel<input value={page.seoTitle ?? ''} onChange={(event) => updatePageField('seoTitle', event.target.value)} /></label>
                <label>SEO-Beschreibung<input value={page.seoDescription ?? ''} onChange={(event) => updatePageField('seoDescription', event.target.value)} /></label>
                <label>OpenGraph Asset
                  <select value={page.ogImageAssetId ?? ''} onChange={(event) => updatePageField('ogImageAssetId', event.target.value || null)}>
                    <option value="">Kein spezielles Bild</option>
                    {assets.map((asset) => <option key={asset.id} value={asset.id}>{asset.title}</option>)}
                  </select>
                </label>
              </div>

              <div className="sectionToolbar">
                <h3>Sektionen</h3>
                <div className="toolbar toolbar--inline">
                  <button className="button button--secondary" onClick={() => addSection('text_block')}>Text</button>
                  <button className="button button--secondary" onClick={() => addSection('feature_grid')}>Features</button>
                  <button className="button button--secondary" onClick={() => addSection('cta')}>CTA</button>
                  <button className="button button--secondary" onClick={() => addSection('legal_text')}>Legal</button>
                </div>
              </div>

              <div className="sectionEditorList">
                {page.sections.map((section, index) => (
                  <SectionEditor
                    key={`${section.id}-${index}`}
                    section={section}
                    index={index}
                    assets={assets}
                    onChange={(next) => updateSection(index, next)}
                    onRemove={() => removeSection(index)}
                    onMoveUp={() => moveSection(index, -1)}
                    onMoveDown={() => moveSection(index, 1)}
                  />
                ))}
              </div>

              <div className="modalActions">
                <button className="button button--ghost" onClick={duplicatePage} disabled={busy}>Duplizieren</button>
                <button className="button button--ghost" onClick={unpublishPage} disabled={busy || page.slug === 'home'}>Zurückziehen</button>
                <button className="button button--danger" onClick={deletePage} disabled={busy || ['home', 'impressum', 'datenschutz'].includes(page.slug)}>Deaktivieren</button>
                <button className="button button--secondary" onClick={savePage} disabled={busy}>Draft speichern</button>
                <button className="button" onClick={publishPage} disabled={busy}>Veröffentlichen</button>
              </div>
            </>
          ) : <p className="muted">Noch keine Website-Seite vorhanden.</p>}
        </main>
      </div>

      <div className="twoColumns">
        <form className="panel" onSubmit={saveSettings}>
          <h2>Website Einstellungen</h2>
          <label>Website Name<input value={settingsDraft?.siteName ?? ''} onChange={(event) => setSettingsDraft((current) => ({ ...(current ?? { siteName: 'Siktra Sleep' }), siteName: event.target.value }))} /></label>
          <label>Default SEO-Titel<input value={settingsDraft?.defaultSeoTitle ?? ''} onChange={(event) => setSettingsDraft((current) => ({ ...(current ?? { siteName: 'Siktra Sleep' }), defaultSeoTitle: event.target.value }))} /></label>
          <label>Default SEO-Beschreibung<textarea value={settingsDraft?.defaultSeoDescription ?? ''} onChange={(event) => setSettingsDraft((current) => ({ ...(current ?? { siteName: 'Siktra Sleep' }), defaultSeoDescription: event.target.value }))} /></label>
          <label>App URL<input value={settingsDraft?.appUrl ?? ''} onChange={(event) => setSettingsDraft((current) => ({ ...(current ?? { siteName: 'Siktra Sleep' }), appUrl: event.target.value }))} /></label>
          <label>Footer Text<input value={settingsDraft?.footerText ?? ''} onChange={(event) => setSettingsDraft((current) => ({ ...(current ?? { siteName: 'Siktra Sleep' }), footerText: event.target.value }))} /></label>
          <button className="button" disabled={busy}>Einstellungen speichern</button>
        </form>

        <form className="panel" onSubmit={createAsset}>
          <h2>Media Library</h2>
          <p className="muted">v0.1 speichert sichere statische oder HTTPS-Asset-Referenzen. R2-Uploads können später additiv ergänzt werden.</p>
          <label>Titel<input value={assetDraft.title ?? ''} onChange={(event) => setAssetDraft((current) => ({ ...current, title: event.target.value }))} /></label>
          <label>Alt-Text<input value={assetDraft.altText ?? ''} onChange={(event) => setAssetDraft((current) => ({ ...current, altText: event.target.value }))} /></label>
          <label>URL<input value={assetDraft.url ?? ''} onChange={(event) => setAssetDraft((current) => ({ ...current, url: event.target.value }))} placeholder="/assets/site/hero-night.svg oder https://..." /></label>
          <button className="button button--secondary" disabled={busy}>Asset anlegen</button>
          <div className="assetList">
            {assets.map((asset) => <div key={asset.id} className="assetItem"><img src={asset.url} alt="" /><div><strong>{asset.title}</strong><span>{asset.url}</span></div></div>)}
          </div>
        </form>
      </div>

      <article className="panel">
        <div className="panelHeader">
          <div><span className="eyebrow">Navigation</span><h2>Header & Footer Links</h2></div>
          <button className="button button--secondary" onClick={saveNavigation} disabled={busy}>Navigation speichern</button>
        </div>
        <div className="navigationEditor">
          {navigationDraft.map((item, index) => (
            <div key={`${item.id ?? 'new'}-${index}`} className="navigationRow">
              <input value={item.label} onChange={(event) => setNavigationDraft((current) => current.map((entry, entryIndex) => entryIndex === index ? { ...entry, label: event.target.value } : entry))} placeholder="Label" />
              <input value={item.href} onChange={(event) => setNavigationDraft((current) => current.map((entry, entryIndex) => entryIndex === index ? { ...entry, href: event.target.value } : entry))} placeholder="Href" />
              <select value={item.placement ?? 'header'} onChange={(event) => setNavigationDraft((current) => current.map((entry, entryIndex) => entryIndex === index ? { ...entry, placement: event.target.value } : entry))}>
                <option value="header">Header</option>
                <option value="footer">Footer</option>
              </select>
              <button className="button button--ghost" onClick={() => setNavigationDraft((current) => current.filter((_, entryIndex) => entryIndex !== index))}>Entfernen</button>
            </div>
          ))}
          <button className="button button--secondary" onClick={() => setNavigationDraft((current) => [...current, { label: 'Neuer Link', href: '/', placement: 'header', target: '_self', isVisible: true, sortOrder: current.length + 1 }])}>Link hinzufügen</button>
        </div>
      </article>
    </section>
  );
}

function SectionEditor({ section, index, assets, onChange, onRemove, onMoveUp, onMoveDown }: { section: WebsiteSection; index: number; assets: WebsiteAsset[]; onChange: (section: WebsiteSection) => void; onRemove: () => void; onMoveUp: () => void; onMoveDown: () => void }) {
  function update<K extends keyof WebsiteSection>(key: K, value: WebsiteSection[K]) {
    onChange({ ...section, [key]: value });
  }

  return (
    <article className="sectionEditor">
      <div className="panelHeader">
        <div><span className="eyebrow">Sektion {index + 1}</span><h3>{sectionLabel(section)}</h3></div>
        <div className="toolbar toolbar--inline">
          <button className="button button--ghost" onClick={onMoveUp}>↑</button>
          <button className="button button--ghost" onClick={onMoveDown}>↓</button>
          <button className="button button--danger" onClick={onRemove}>Entfernen</button>
        </div>
      </div>
      <div className="formGrid">
        <label>ID<input value={section.id ?? ''} onChange={(event) => update('id', event.target.value)} /></label>
        <label>Typ
          <select value={section.type} onChange={(event) => update('type', event.target.value)}>
            <option value="hero">Hero</option>
            <option value="text_block">Text</option>
            <option value="feature_grid">Feature Grid</option>
            <option value="image_text">Bild + Text</option>
            <option value="trust_panel">Trust Panel</option>
            <option value="cta">CTA</option>
            <option value="faq">FAQ</option>
            <option value="legal_text">Legal Text</option>
          </select>
        </label>
        <label>Eyebrow<input value={section.eyebrow ?? ''} onChange={(event) => update('eyebrow', event.target.value)} /></label>
        <label>Titel<input value={section.title ?? ''} onChange={(event) => update('title', event.target.value)} /></label>
        <label className="wideField">Text<textarea value={section.body ?? ''} onChange={(event) => update('body', event.target.value)} /></label>
        <label>Bild Asset
          <select value={section.imageAssetId ?? ''} onChange={(event) => update('imageAssetId', event.target.value || null)}>
            <option value="">Kein Bild</option>
            {assets.map((asset) => <option key={asset.id} value={asset.id}>{asset.title}</option>)}
          </select>
        </label>
        <label>Bild URL Fallback<input value={section.imageUrl ?? ''} onChange={(event) => update('imageUrl', event.target.value)} /></label>
        <label>Bild Alt-Text<input value={section.imageAlt ?? ''} onChange={(event) => update('imageAlt', event.target.value)} /></label>
        <label>Primary CTA Label<input value={section.primaryCtaLabel ?? ''} onChange={(event) => update('primaryCtaLabel', event.target.value)} /></label>
        <label>Primary CTA Link<input value={section.primaryCtaHref ?? ''} onChange={(event) => update('primaryCtaHref', event.target.value)} /></label>
      </div>
      {section.type === 'feature_grid' || section.type === 'faq' ? <ItemsEditor section={section} onChange={onChange} /> : null}
    </article>
  );
}

function ItemsEditor({ section, onChange }: { section: WebsiteSection; onChange: (section: WebsiteSection) => void }) {
  const items = section.items ?? [];

  function updateItem(index: number, key: string, value: string) {
    const next = items.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item);
    onChange({ ...section, items: next });
  }

  return (
    <div className="itemsEditor">
      <div className="panelHeader">
        <h4>{section.type === 'faq' ? 'FAQ Einträge' : 'Feature Einträge'}</h4>
        <button className="button button--ghost" onClick={() => onChange({ ...section, items: [...items, { title: 'Neuer Eintrag', body: 'Beschreibung' }] })}>Eintrag hinzufügen</button>
      </div>
      {items.map((item, index) => (
        <div className="itemEditor" key={index}>
          <input value={String(item.title ?? '')} onChange={(event) => updateItem(index, 'title', event.target.value)} placeholder="Titel/Frage" />
          <textarea value={String(item.body ?? '')} onChange={(event) => updateItem(index, 'body', event.target.value)} placeholder="Text/Antwort" />
          <button className="button button--ghost" onClick={() => onChange({ ...section, items: items.filter((_, itemIndex) => itemIndex !== index) })}>Entfernen</button>
        </div>
      ))}
    </div>
  );
}
